import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import api from '../../utils/api';
import { useAuth } from '../../App';
import {
  Archive, Download, Upload, CheckCircle2, Search,
  ChevronDown, ChevronRight, FileText, Calendar,
  ChevronLeft, Eye, Trash2, Loader2, X, Filter, Zap, Landmark, Receipt, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { handleNameCorrections } from '../../utils/nameCorrections';
import PayPalOwnerPaymentsTab from './payments/PayPalOwnerPaymentsTab';
import DuplicatesCheckModal from './DuplicatesCheckModal';
import TouristTaxArchiveTab from './payments/TouristTaxArchiveTab';
import IntercompanyChargesTab from './payments/IntercompanyChargesTab';
import OwnerReconstructionTab from './payments/OwnerReconstructionTab';

// ─── Shared helpers ───
const fmtEur = (v) => Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return d; }
};
const fmtShortDate = (d) => {
  if (!d) return '';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return d; }
};

// ─── Beneficiaries table (shared by both tabs) ───
function BeneficiariesTable({ batch, expandedBenef, setExpandedBenef, onReceipt }) {
  return (
    <div className="max-h-[500px] overflow-y-auto border rounded-lg">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 sticky top-0 z-10"><tr>
          <th className="text-left p-2 w-8"></th>
          <th className="text-left p-2 font-medium text-slate-600">Beneficiario</th>
          <th className="text-left p-2 font-medium text-slate-600">IBAN</th>
          <th className="text-right p-2 font-medium text-slate-600">N.</th>
          <th className="text-right p-2 font-medium text-slate-600">Totale</th>
          {onReceipt && <th className="text-right p-2 font-medium text-slate-600 w-24"></th>}
        </tr></thead>
        <tbody className="divide-y">
          {(batch.beneficiaries || []).map((ben, i) => {
            const bk = `${batch.id}_${ben.name}`;
            const bo = expandedBenef[bk];
            return (
              <React.Fragment key={i}>
                <tr className={`cursor-pointer transition-colors ${bo ? 'bg-blue-50' : 'hover:bg-slate-50'}`} onClick={() => setExpandedBenef(p => ({ ...p, [bk]: !bo }))}>
                  <td className="p-2">{bo ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}</td>
                  <td className="p-2 font-medium">{ben.name}</td>
                  <td className="p-2 text-slate-500 font-mono text-xs">{ben.iban}</td>
                  <td className="p-2 text-right">{ben.count}</td>
                  <td className="p-2 text-right font-semibold">{fmtEur(ben.total)}</td>
                  {onReceipt && (
                    <td className="p-2 text-right">
                      <button data-testid={`receipt-ben-${batch.id}-${i}`} title="Vedi/stampa SOLO la ricevuta di questo beneficiario"
                        onClick={(e) => { e.stopPropagation(); onReceipt(ben.name, null); }}
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded border border-blue-200 transition-colors">
                        <Receipt className="h-3.5 w-3.5" />Ricevuta
                      </button>
                    </td>
                  )}
                </tr>
                {bo && ben.transactions && (
                  <tr><td colSpan={onReceipt ? 6 : 5} className="p-0"><div className="bg-slate-50 border-t border-b">
                    <table className="w-full text-xs"><thead><tr className="text-slate-500">
                      <th className="pl-10 pr-2 py-1.5 text-left font-medium">Riferimento</th>
                      <th className="px-2 py-1.5 text-left font-medium">Causale</th>
                      <th className="px-2 py-1.5 text-right font-medium pr-3">Importo</th>
                      {onReceipt && <th className="px-2 py-1.5 text-right font-medium pr-3 w-16"></th>}
                    </tr></thead><tbody className="divide-y divide-slate-100">
                      {ben.transactions.map((tx, j) => (
                        <tr key={j} className="hover:bg-slate-100/50">
                          <td className="pl-10 pr-2 py-1.5 font-mono text-slate-700">{tx.reference || '-'}</td>
                          <td className="px-2 py-1.5 text-slate-600 truncate max-w-[300px]" title={tx.causale}>{tx.causale || '-'}</td>
                          <td className="px-2 py-1.5 text-right font-medium text-slate-800 pr-3">{fmtEur(tx.amount)}</td>
                          {onReceipt && (
                            <td className="px-2 py-1.5 text-right pr-3">
                              <button data-testid={`receipt-tx-${batch.id}-${i}-${j}`} title="Ricevuta di questo singolo bonifico"
                                onClick={(e) => { e.stopPropagation(); onReceipt(ben.name, tx.amount); }}
                                className="text-blue-500 hover:text-blue-700 p-0.5">
                                <Receipt className="h-3.5 w-3.5" />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody></table>
                  </div></td></tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── PDF Preview Modal ───
function PdfPreviewModal({ pdfPreview, onClose }) {
  if (!pdfPreview) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b">
          <h3 className="font-semibold text-slate-900">{pdfPreview.title || 'Risposta Banca'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded"><X className="h-5 w-5" /></button>
        </div>
        <iframe src={pdfPreview.url} className="flex-1 w-full" title="Risposta Banca" />
      </div>
    </div>
  );
}

export default function PaymentArchivePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const canMutatePaypal = user?.role === 'admin' || user?.role === 'customer_service';
  const [activeTab, setActiveTab] = useState('owners');
  const [year, setYear] = useState(new Date().getFullYear());
  const [pdfPreview, setPdfPreview] = useState(null);
  const [rescanningNames, setRescanningNames] = useState(false);
  const dupParam = new URLSearchParams(window.location.search).get('doppioni');
  const [showDupCheck, setShowDupCheck] = useState(!!dupParam);
  const [dupAutoPrint] = useState(dupParam === 'print');

  const handleRescanNames = async () => {
    setRescanningNames(true);
    try {
      const res = await api.post('/payment-archive/rescan-names?days=30');
      const nc = res.data?.name_corrections || {};
      if (!(nc.auto || []).length && !(nc.suggestions || []).length) {
        toast.info(`Nessuna correzione: ${res.data?.batches_scanned || 0} contabili ricontrollate, nomi già allineati`);
      }
      await handleNameCorrections(nc);
    } catch (e) {
      toast.error('Errore riscansione nomi banca');
    } finally {
      setRescanningNames(false);
    }
  };

  // ─── Owner batches state ───
  const [ownerBatches, setOwnerBatches] = useState([]);
  const [bookingBatches, setBookingBatches] = useState([]);
  const [ownerLoading, setOwnerLoading] = useState(true);
  const [ownerSearch, setOwnerSearch] = useState('');
  const [ownerFilterProp, setOwnerFilterProp] = useState('');
  const [ownerFilterBen, setOwnerFilterBen] = useState('');
  const [ownerFilterBooking, setOwnerFilterBooking] = useState('');
  const [ownerFilterStayFrom, setOwnerFilterStayFrom] = useState('');
  const [ownerFilterStayTo, setOwnerFilterStayTo] = useState('');
  const [ownerFilterType, setOwnerFilterType] = useState('all'); // all|instant|ordinary
  const [showOwnerFilters, setShowOwnerFilters] = useState(false);
  const [editingDates, setEditingDates] = useState(null);
  const [editFrom, setEditFrom] = useState('');
  const [editTo, setEditTo] = useState('');

  // ─── Invoice batches state ───
  const [invBatches, setInvBatches] = useState([]);
  const [invLoading, setInvLoading] = useState(true);
  const [invSearch, setInvSearch] = useState('');
  const [invFilterBen, setInvFilterBen] = useState('');
  const [invFilterType, setInvFilterType] = useState('all'); // all|instant|ordinary
  const [showInvFilters, setShowInvFilters] = useState(false);

  // ─── Booking.com batches filters (client-side, multi-criteri) ───
  const [bookSearch, setBookSearch] = useState('');
  const [bookFilterStatus, setBookFilterStatus] = useState('all');  // all|pending|executed
  const [bookFilterMonth, setBookFilterMonth] = useState('all');
  const [bookFilterType, setBookFilterType] = useState('all'); // all|instant|ordinary
  const [bookAmountMin, setBookAmountMin] = useState('');
  const [bookAmountMax, setBookAmountMax] = useState('');
  const [bookDateFrom, setBookDateFrom] = useState('');
  const [bookDateTo, setBookDateTo] = useState('');
  const [showBookFilters, setShowBookFilters] = useState(false);

  // Shared
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [expandedBenef, setExpandedBenef] = useState({});
  const [yearlyTotals, setYearlyTotals] = useState(null);

  // ─── Fetch totali annui ───
  const fetchYearlyTotals = async (y) => {
    try {
      const r = await api.get(`/payment-archive/yearly-totals?year=${y}`);
      setYearlyTotals(r.data);
    } catch (e) { /* silent fail */ }
  };

  useEffect(() => { fetchYearlyTotals(year); /* eslint-disable-next-line */ }, [year]);

  // ─── Fetch functions ───
  const fetchOwnerBatches = async () => {
    setOwnerLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('year', year);
      if (ownerSearch) p.append('search', ownerSearch);
      if (ownerFilterProp) p.append('property_name', ownerFilterProp);
      if (ownerFilterBen) p.append('beneficiary', ownerFilterBen);
      if (ownerFilterBooking) p.append('booking_code', ownerFilterBooking);
      if (ownerFilterStayFrom) p.append('stay_from', ownerFilterStayFrom);
      if (ownerFilterStayTo) p.append('stay_to', ownerFilterStayTo);
      const res = await api.get(`/payment-batches?${p}`);
      const all = res.data.batches || [];
      // Split: owners (non-booking) vs booking commissions (include anche i lotti "_split")
      const isBooking = (b) => (b.batch_type || '').startsWith('booking_commissions');
      setOwnerBatches(all.filter(b => !isBooking(b)));
      setBookingBatches(all.filter(isBooking));
    } catch { toast.error('Errore caricamento proprietari'); } finally { setOwnerLoading(false); }
  };

  const fetchInvBatches = async () => {
    setInvLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('year', year);
      if (invSearch) p.append('search', invSearch);
      if (invFilterBen) p.append('beneficiary', invFilterBen);
      const res = await api.get(`/invoice-payment-batches?${p}`);
      setInvBatches(res.data.batches || []);
    } catch { toast.error('Errore caricamento fatture'); } finally { setInvLoading(false); }
  };

  useEffect(() => { fetchOwnerBatches(); fetchInvBatches(); }, [year]);

  // ─── Owner actions ───
  const handleOwnerDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo batch?')) return;
    try { await api.delete(`/admin/payment-batches/${id}`); toast.success('Eliminato'); fetchOwnerBatches(); }
    catch { toast.error('Errore'); }
  };
  const describeCourtesyStats = (s) => {
    if (!s || Object.keys(s).length === 0) return null;
    const okCount = (s.sent || 0) + (s.queued || 0);
    if (okCount > 0) return { ok: true, msg: `${okCount} mail di cortesia ${(s.sent || 0) > 0 ? 'inviate ai proprietari' : 'in coda di invio'}` };
    const problems = [];
    if (s.unmatched_count) problems.push(`${s.unmatched_count} riferimenti non abbinati alle prenotazioni${(s.unmatched_refs || []).length ? ` (es. ${s.unmatched_refs.slice(0, 3).join(', ')})` : ''}`);
    if (s.already_sent) problems.push(`${s.already_sent} già inviate in precedenza`);
    if (s.no_owner) problems.push(`${s.no_owner} senza email proprietario`);
    if (s.failed) problems.push(`${s.failed} fallite`);
    return { ok: false, msg: problems.length ? `nessuna mail di cortesia inviata: ${problems.join('; ')}` : 'nessuna mail di cortesia da inviare' };
  };
  const notifyPaypalAuto = (pa) => {
    if (pa?.marked > 0) {
      const one = pa.marked === 1;
      toast.info(`💰 ${pa.marked} pagament${one ? 'o' : 'i'} PayPal marcat${one ? 'o' : 'i'} automaticamente come pagat${one ? 'o' : 'i'} (saldati con questo bonifico): ${(pa.codes || []).join(', ')}`, { duration: 12000 });
    }
  };
  const handleOwnerMarkExecuted = async (id) => {
    try {
      const res = await api.post(`/admin/payment-batches/${id}/mark-executed`);
      const info = describeCourtesyStats(res.data?.email_stats);
      if (info && !info.ok) toast.warning(`Eseguito — ${info.msg}`, { duration: 10000 });
      else toast.success(info ? `Eseguito — ${info.msg}` : 'Eseguito');
      notifyPaypalAuto(res.data?.paypal_auto);
      fetchOwnerBatches();
    } catch { toast.error('Errore'); }
  };
  const handleOwnerUploadResponse = async (id, file, batchStatus) => {
    const fd = new FormData(); fd.append('file', file);
    let sendEmails = true;
    if (batchStatus !== 'executed') {
      sendEmails = window.confirm('Inviare anche le email di cortesia ai proprietari?\n\nOK = invia le email\nAnnulla = carica solo il PDF senza email (es. proprietari già avvisati)');
    }
    try {
      const res = await api.post(`/admin/payment-batches/${id}/upload-response?send_emails=${sendEmails}`, fd);
      if (res.data?.already_executed) toast.success('PDF banca sostituito ✅');
      else if (!sendEmails) toast.success('Risposta caricata — nessuna email inviata (come richiesto)');
      else {
        const info = describeCourtesyStats(res.data?.email_stats);
        if (info && !info.ok) toast.warning(`Risposta caricata — ${info.msg}`, { duration: 10000 });
        else toast.success(info ? `Risposta caricata — ${info.msg}` : 'Risposta caricata');
      }
      notifyPaypalAuto(res.data?.paypal_auto);
      handleNameCorrections(res.data?.name_corrections);
      fetchOwnerBatches();
    } catch { toast.error('Errore'); }
  };
  const handleOwnerDownload = async (id, type, fname) => {
    try {
      const res = await api.get(`/admin/payment-batches/${id}/download/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = fname || `file_${type}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };
  const handleOwnerViewPdf = async (id) => {
    try {
      const res = await api.get(`/admin/payment-batches/${id}/download/bank_response?inline=true`, { responseType: 'blob' });
      setPdfPreview({ url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), title: 'Risposta Banca' });
    } catch { toast.error('Errore'); }
  };
  const handleViewReceipt = async (kind, batchId, benName, amount = null) => {
    try {
      const p = new URLSearchParams({ beneficiary: benName });
      if (amount != null) p.append('amount', amount);
      const res = await api.get(`/payment-archive/${kind}/${batchId}/receipt?${p.toString()}`, { responseType: 'blob' });
      setPdfPreview({ url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), title: `Ricevuta — ${benName}` });
    } catch (e) {
      let msg = 'Nessuna ricevuta abbinata a questo beneficiario nel PDF banca';
      try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* noop */ }
      toast.error(msg, { duration: 7000 });
    }
  };

  const handleSaveDates = async (batchId) => {
    try { await api.patch(`/admin/payment-batches/${batchId}`, { stay_date_from: editFrom, stay_date_to: editTo }); toast.success('Date aggiornate'); setEditingDates(null); fetchOwnerBatches(); }
    catch { toast.error('Errore'); }
  };

  // ─── Invoice actions ───
  const handleInvDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Eliminare questo batch fatture?')) return;
    try { await api.delete(`/invoice-payment-batches/${id}`); toast.success('Eliminato'); fetchInvBatches(); }
    catch { toast.error('Errore'); }
  };
  const handleInvMarkExecuted = async (id) => {
    try {
      const res = await api.post(`/invoice-payment-batches/${id}/mark-executed`);
      toast.success(res.data.message || 'Eseguito'); fetchInvBatches();
    } catch { toast.error('Errore'); }
  };
  const handleInvUploadResponse = async (id, file) => {
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post(`/invoice-payment-batches/${id}/upload-response`, fd);
      toast.success(res.data.message || 'Risposta caricata'); fetchInvBatches();
    } catch { toast.error('Errore'); }
  };
  const handleInvDownload = async (id, type, fname) => {
    try {
      const res = await api.get(`/invoice-payment-batches/${id}/download/${type}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = fname || `file_${type}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Errore download'); }
  };
  const handleInvViewPdf = async (id) => {
    try {
      const res = await api.get(`/invoice-payment-batches/${id}/download/bank_response?inline=true`, { responseType: 'blob' });
      setPdfPreview({ url: URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' })), title: 'Risposta Banca' });
    } catch { toast.error('Errore'); }
  };

  const ownerHasFilters = ownerSearch || ownerFilterProp || ownerFilterBen || ownerFilterBooking || ownerFilterStayFrom || ownerFilterStayTo || ownerFilterType !== 'all';
  const invHasFilters = invSearch || invFilterBen || invFilterType !== 'all';

  // ─── Filtro Tipo bonifico (client-side: il campo transfer_type è presente solo sui lotti nuovi) ───
  const filterByType = (list, t) => (t === 'all' ? list : list.filter(b => (b.transfer_type || '') === t));
  const visibleOwnerBatches = filterByType(ownerBatches, ownerFilterType);
  const visibleInvBatches = filterByType(invBatches, invFilterType);

  // ─── Batch row renderer ───
  const renderBatchRow = (batch, { onDelete, onMarkExecuted, onUploadResponse, onDownload, onViewPdf, isOwner }) => {
    const isExp = expandedBatch === batch.id;
    const isExec = batch.status === 'executed';
    const fmtMonth = (d) => { try { return new Date(d + '-01').toLocaleDateString('it-IT', {month:'long', year:'numeric'}); } catch { return d; } };
    const stayLabel = isOwner && (batch.batch_type || '').startsWith('booking_commissions')
      ? ((batch.stay_date_from || batch.stay_date_to) ? `Fattura ${fmtMonth(batch.stay_date_from)} - Rif. ${fmtMonth(batch.stay_date_to)}` : '')
      : isOwner && (batch.stay_date_from || batch.stay_date_to)
        ? `dal ${fmtShortDate(batch.stay_date_from) || '...'} al ${fmtShortDate(batch.stay_date_to) || '...'}`
        : '';

    return (
      <Card key={batch.id} className="overflow-hidden" data-testid={`batch-${batch.id}`}>
        <div className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors ${isExp ? 'bg-slate-50 border-b' : ''}`}
          onClick={() => setExpandedBatch(isExp ? null : batch.id)}>
          {isExp ? <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />}
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold shrink-0 ${isExec ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {isExec ? (isOwner ? 'Eseguito' : 'Pagato') : 'Generato'}
          </span>
          <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
            <span className="text-sm text-slate-600 flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(batch.created_at)}</span>
            {batch.filtered ? (
              <>
                <span className="text-sm font-medium text-blue-700">{batch.filtered_transactions} su {batch.total_transactions} bonifici</span>
                <span className="text-sm text-blue-600">{batch.filtered_beneficiaries} beneficiari</span>
              </>
            ) : (
              <>
                <span className="text-sm text-slate-600">{batch.total_transactions} bonifici</span>
                <span className="text-sm text-slate-600">{batch.total_beneficiaries} beneficiari</span>
              </>
            )}
            {isOwner && (batch.batch_type || '').startsWith('booking_commissions') && <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Booking.com</span>}
            {!isOwner && <span className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded">Fatture</span>}
            {batch.transfer_type === 'instant' && (
              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1 font-medium" data-testid={`batch-transfer-type-${batch.id}`}>
                <Zap className="h-3 w-3" />Istantaneo
              </span>
            )}
            {batch.transfer_type === 'ordinary' && (
              <span className="text-xs bg-slate-200 text-slate-700 px-1.5 py-0.5 rounded inline-flex items-center gap-1 font-medium" data-testid={`batch-transfer-type-${batch.id}`}>
                <Landmark className="h-3 w-3" />Ordinario
              </span>
            )}
            {stayLabel ? (
              <span className="text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full">{stayLabel}</span>
            ) : isOwner && isAdmin && (
              <button onClick={(e) => { e.stopPropagation(); setEditingDates(batch.id); setEditFrom(batch.stay_date_from || ''); setEditTo(batch.stay_date_to || ''); }}
                className="text-xs text-slate-400 hover:text-blue-600 px-2 py-0.5 border border-dashed rounded-full">+ Date soggiorno</button>
            )}
          </div>
          <div className="text-right shrink-0">
            {batch.filtered ? (
              <><span className="text-lg font-bold text-blue-700">{fmtEur(batch.filtered_total)}</span><span className="text-xs text-slate-400 block">su {fmtEur(batch.total_amount)}</span></>
            ) : (
              <span className="text-lg font-bold text-slate-900">{fmtEur(batch.total_amount)}</span>
            )}
          </div>
          {isAdmin && <button onClick={(e) => onDelete(e, batch.id)} className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors shrink-0" title="Elimina"><Trash2 className="h-4 w-4" /></button>}
        </div>

        {isExp && (
          <div className="p-4 space-y-3">
            <div className="flex items-center gap-2 flex-wrap pb-3 border-b">
              {isAdmin && isOwner && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onDownload(batch.id, 'sabadell', batch.sabadell_filename)}><Download className="h-3.5 w-3.5 mr-1" />File Originale</Button>
                  <Button size="sm" variant="outline" onClick={() => onDownload(batch.id, 'cbi', batch.cbi_filename)}><Download className="h-3.5 w-3.5 mr-1" />XML CBI</Button>
                </>
              )}
              {isAdmin && !isOwner && (
                <Button size="sm" variant="outline" onClick={() => onDownload(batch.id, 'cbi', batch.cbi_filename)}><Download className="h-3.5 w-3.5 mr-1" />XML CBI</Button>
              )}
              {batch.bank_response_filename && (
                <>
                  <Button size="sm" variant="outline" onClick={() => onViewPdf(batch.id)}><Eye className="h-3.5 w-3.5 mr-1" />Visualizza Risposta</Button>
                  <Button size="sm" variant="outline" onClick={() => onDownload(batch.id, 'bank_response', batch.bank_response_filename)}><Download className="h-3.5 w-3.5 mr-1" />Scarica Risposta</Button>
                </>
              )}
              <label className="cursor-pointer" data-testid={`batch-upload-response-${batch.id}`}>
                <input type="file" className="hidden" accept=".pdf" onChange={(e) => e.target.files[0] && onUploadResponse(batch.id, e.target.files[0], batch.status)} />
                <span className="inline-flex items-center px-3 py-1.5 text-sm border rounded-lg hover:bg-slate-50">
                  <Upload className="h-3.5 w-3.5 mr-1" />{batch.bank_response_filename ? 'Sostituisci PDF Banca' : 'Carica PDF Banca'}
                </span>
              </label>
              {!isExec && (
                <Button size="sm" onClick={() => onMarkExecuted(batch.id)} className="bg-green-600 hover:bg-green-700"><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{isOwner ? 'Eseguito' : 'Segna Pagato'}</Button>
              )}
              <span className="ml-auto text-xs text-slate-400">Da: {batch.created_by_name || batch.created_by}</span>
            </div>
            <BeneficiariesTable batch={batch} expandedBenef={expandedBenef} setExpandedBenef={setExpandedBenef}
              onReceipt={batch.bank_response_filename ? (ben, amount) => handleViewReceipt(isOwner ? 'owner' : 'invoice', batch.id, ben, amount) : null} />
          </div>
        )}
      </Card>
    );
  };

  return (
    <Layout title="Archivio Pagamenti">
      <div className="space-y-4" data-testid="payment-archive-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Archive className="h-5 w-5 text-blue-600" /> Archivio Pagamenti
          </h2>
          <div className="flex items-center gap-3">
            <Button size="sm" variant="outline" data-testid="duplicates-check-btn"
                    onClick={() => setShowDupCheck(true)}
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    title="Cerca pagamenti/fatture duplicati in tutte le sezioni (fatture, batch bonifici, prenotazioni, segnalatori, preventivi)">
              Controllo Doppioni
            </Button>
            <Button size="sm" variant="outline" data-testid="rescan-bank-names-btn"
                    onClick={handleRescanNames} disabled={rescanningNames}
                    title="Ricontrolla le contabili banca degli ultimi 30 giorni (proprietari, fatture, tassa di soggiorno) e correggi i nomi in rubrica/regole">
              {rescanningNames ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Ricontrolla nomi banca
            </Button>
            <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
              <button onClick={() => setYear(y => y - 1)} className="p-1.5 hover:bg-white rounded transition-colors" data-testid="year-prev"><ChevronLeft className="h-4 w-4" /></button>
              <span className="px-3 py-1 font-bold text-slate-800 min-w-[60px] text-center" data-testid="year-display">{year}</span>
              <button onClick={() => setYear(y => y + 1)} className="p-1.5 hover:bg-white rounded transition-colors" data-testid="year-next"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1" data-testid="archive-tabs">
          <button
            data-testid="tab-owners"
            onClick={() => setActiveTab('owners')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'owners' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Proprietari
            {ownerBatches.length > 0 && <span className="ml-2 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">{ownerBatches.length}</span>}
          </button>
          <button
            data-testid="tab-invoices"
            onClick={() => setActiveTab('invoices')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'invoices' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            Fatture
            {invBatches.length > 0 && <span className="ml-2 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full text-xs">{invBatches.length}</span>}
          </button>
          {isAdmin && (
            <button
              data-testid="tab-booking"
              onClick={() => setActiveTab('booking')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'booking' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              Booking.com
              {bookingBatches.length > 0 && <span className="ml-2 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-xs">{bookingBatches.length}</span>}
            </button>
          )}
          <button
            data-testid="tab-paypal"
            onClick={() => setActiveTab('paypal')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'paypal' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            💰 PayPal
          </button>
          <button
            data-testid="tab-ids"
            onClick={() => setActiveTab('ids')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'ids' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            🏛️ Tassa di Soggiorno
          </button>
          <button
            data-testid="tab-reconstruction"
            onClick={() => setActiveTab('reconstruction')}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'reconstruction' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
          >
            🔎 Ricostruzione Proprietario
          </button>
          {isAdmin && (
            <button
              data-testid="tab-intercompany"
              onClick={() => setActiveTab('intercompany')}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'intercompany' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              🔁 Ribaltamenti
            </button>
          )}
        </div>

        {/* ═══ OWNERS TAB ═══ */}
        {activeTab === 'owners' && (
          <>
            {yearlyTotals && (
              <Card className="p-3 mb-3 bg-blue-50 border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-blue-900">
                    <span className="text-sm">Totale Proprietari pagato {year}:</span>
                    <span className="text-xl font-bold">€ {Number(yearlyTotals.owners_total || 0).toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>
              </Card>
            )}
            <div className="flex justify-end">
              <Button size="sm" variant={showOwnerFilters ? 'default' : 'outline'} onClick={() => setShowOwnerFilters(!showOwnerFilters)}>
                <Filter className="h-3.5 w-3.5 mr-1.5" /> Filtri {ownerHasFilters && <span className="ml-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
              </Button>
            </div>
            {showOwnerFilters && (
              <Card className="p-4">
                <form onSubmit={(e) => { e.preventDefault(); fetchOwnerBatches(); }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Ricerca</label>
                    <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" /><input type="text" value={ownerSearch} onChange={(e) => setOwnerSearch(e.target.value)} placeholder="Cerca..." className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm" /></div></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Immobile</label>
                    <input type="text" value={ownerFilterProp} onChange={(e) => setOwnerFilterProp(e.target.value)} placeholder="Nome immobile..." className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Beneficiario</label>
                    <input type="text" value={ownerFilterBen} onChange={(e) => setOwnerFilterBen(e.target.value)} placeholder="Nome beneficiario..." className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Codice prenotazione</label>
                    <input type="text" value={ownerFilterBooking} onChange={(e) => setOwnerFilterBooking(e.target.value)} placeholder="ILA..." className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Soggiorno dal</label>
                    <input type="date" value={ownerFilterStayFrom} onChange={(e) => setOwnerFilterStayFrom(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Soggiorno al</label>
                    <input type="date" value={ownerFilterStayTo} onChange={(e) => setOwnerFilterStayTo(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Tipo bonifico</label>
                    <select value={ownerFilterType} onChange={(e) => setOwnerFilterType(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="owner-filter-transfer-type">
                      <option value="all">Tutti i tipi</option>
                      <option value="instant">Istantanei</option>
                      <option value="ordinary">Ordinari</option>
                    </select></div>
                  <div className="flex items-end gap-2 lg:col-span-3">
                    <Button type="submit" size="sm"><Search className="h-3.5 w-3.5 mr-1" /> Cerca</Button>
                    {ownerHasFilters && <Button type="button" size="sm" variant="outline" onClick={() => { setOwnerSearch(''); setOwnerFilterProp(''); setOwnerFilterBen(''); setOwnerFilterBooking(''); setOwnerFilterStayFrom(''); setOwnerFilterStayTo(''); setOwnerFilterType('all'); setTimeout(fetchOwnerBatches, 50); }}><X className="h-3.5 w-3.5 mr-1" /> Reset</Button>}
                  </div>
                </form>
              </Card>
            )}
            {ownerLoading ? (
              <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
            ) : visibleOwnerBatches.length === 0 ? (
              <Card className="p-8 text-center text-slate-400"><Archive className="h-8 w-8 mx-auto mb-2" /><p>{ownerBatches.length === 0 ? `Nessun pagamento proprietari per ${year}` : 'Nessun risultato per i filtri impostati'}</p></Card>
            ) : (
              <div className="space-y-2">
                {visibleOwnerBatches.map(b => renderBatchRow(b, { onDelete: handleOwnerDelete, onMarkExecuted: handleOwnerMarkExecuted, onUploadResponse: handleOwnerUploadResponse, onDownload: handleOwnerDownload, onViewPdf: handleOwnerViewPdf, isOwner: true }))}
              </div>
            )}
          </>
        )}

        {/* ═══ INVOICES TAB ═══ */}
        {activeTab === 'invoices' && (
          <>
            {yearlyTotals && (
              <Card className="p-3 mb-3 bg-purple-50 border-purple-200">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 text-purple-900">
                    <span className="text-sm">Totale Fatture pagate {year}:</span>
                    <span className="text-xl font-bold">€ {Number(yearlyTotals.invoices_total || 0).toFixed(2).replace('.', ',')}</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="bg-white/60 rounded px-2 py-1 border border-purple-100">
                    <div className="text-purple-700">Partner</div>
                    <div className="font-bold text-purple-900">€ {Number(yearlyTotals.invoices_by_natura?.partner || 0).toFixed(2).replace('.', ',')}</div>
                  </div>
                  <div className="bg-white/60 rounded px-2 py-1 border border-purple-100">
                    <div className="text-purple-700">Responsabili</div>
                    <div className="font-bold text-purple-900">€ {Number(yearlyTotals.invoices_by_natura?.responsabile || 0).toFixed(2).replace('.', ',')}</div>
                  </div>
                  <div className="bg-white/60 rounded px-2 py-1 border border-purple-100">
                    <div className="text-purple-700">Fornitori</div>
                    <div className="font-bold text-purple-900">€ {Number(yearlyTotals.invoices_by_natura?.fornitore || 0).toFixed(2).replace('.', ',')}</div>
                  </div>
                  {(yearlyTotals.invoices_by_natura?.da_classificare > 0) && (
                    <div className="bg-amber-50 rounded px-2 py-1 border border-amber-100">
                      <div className="text-amber-700">Non classificate</div>
                      <div className="font-bold text-amber-900">€ {Number(yearlyTotals.invoices_by_natura.da_classificare).toFixed(2).replace('.', ',')}</div>
                    </div>
                  )}
                </div>
              </Card>
            )}
            <div className="flex justify-end">
              <Button size="sm" variant={showInvFilters ? 'default' : 'outline'} onClick={() => setShowInvFilters(!showInvFilters)}>
                <Filter className="h-3.5 w-3.5 mr-1.5" /> Filtri {invHasFilters && <span className="ml-1 bg-red-500 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center">!</span>}
              </Button>
            </div>
            {showInvFilters && (
              <Card className="p-4">
                <form onSubmit={(e) => { e.preventDefault(); fetchInvBatches(); }} className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Ricerca</label>
                    <div className="relative"><Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" /><input type="text" value={invSearch} onChange={(e) => setInvSearch(e.target.value)} placeholder="Cerca fattura, causale..." className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm" /></div></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Beneficiario</label>
                    <input type="text" value={invFilterBen} onChange={(e) => setInvFilterBen(e.target.value)} placeholder="Nome beneficiario..." className="w-full px-3 py-2 border rounded-lg text-sm" /></div>
                  <div><label className="block text-xs font-medium text-slate-600 mb-1">Tipo bonifico</label>
                    <select value={invFilterType} onChange={(e) => setInvFilterType(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm bg-white" data-testid="invoice-filter-transfer-type">
                      <option value="all">Tutti i tipi</option>
                      <option value="instant">Istantanei</option>
                      <option value="ordinary">Ordinari</option>
                    </select></div>
                  <div className="flex items-end gap-2">
                    <Button type="submit" size="sm"><Search className="h-3.5 w-3.5 mr-1" /> Cerca</Button>
                    {invHasFilters && <Button type="button" size="sm" variant="outline" onClick={() => { setInvSearch(''); setInvFilterBen(''); setInvFilterType('all'); setTimeout(fetchInvBatches, 50); }}><X className="h-3.5 w-3.5 mr-1" /> Reset</Button>}
                  </div>
                </form>
              </Card>
            )}
            {invLoading ? (
              <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
            ) : visibleInvBatches.length === 0 ? (
              <Card className="p-8 text-center text-slate-400"><FileText className="h-8 w-8 mx-auto mb-2" /><p>{invBatches.length === 0 ? `Nessun pagamento fatture per ${year}` : 'Nessun risultato per i filtri impostati'}</p></Card>
            ) : (
              <div className="space-y-2">
                {visibleInvBatches.map(b => renderBatchRow(b, { onDelete: handleInvDelete, onMarkExecuted: handleInvMarkExecuted, onUploadResponse: handleInvUploadResponse, onDownload: handleInvDownload, onViewPdf: handleInvViewPdf, isOwner: false }))}
              </div>
            )}
          </>
        )}


        {/* ═══ BOOKING TAB (Admin only) ═══ */}
        {activeTab === 'booking' && isAdmin && (
          <>
            {yearlyTotals && (
              <Card className="p-3 mb-3 bg-cyan-50 border-cyan-200">
                <div className="flex items-center gap-2 text-cyan-900">
                  <span className="text-sm">Totale Booking.com pagato {year}:</span>
                  <span className="text-xl font-bold">€ {Number(yearlyTotals.booking_total || 0).toFixed(2).replace('.', ',')}</span>
                </div>
              </Card>
            )}
            {/* Filtri intelligenti Booking.com */}
            <Card className="p-3 mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[260px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cerca su tutto (importo, beneficiario, IBAN, fattura, data, nº bonifici…)"
                    value={bookSearch}
                    onChange={(e) => setBookSearch(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border rounded-lg text-sm"
                    data-testid="booking-search-input"
                  />
                </div>
                <select
                  value={bookFilterStatus}
                  onChange={(e) => setBookFilterStatus(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-white"
                  data-testid="booking-filter-status"
                >
                  <option value="all">Tutti gli stati</option>
                  <option value="pending">In attesa</option>
                  <option value="executed">Eseguiti</option>
                </select>
                <select
                  value={bookFilterMonth}
                  onChange={(e) => setBookFilterMonth(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-white"
                  data-testid="booking-filter-month"
                >
                  <option value="all">Tutti i mesi</option>
                  {['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'].map((m, idx) => (
                    <option key={idx} value={idx + 1}>{m}</option>
                  ))}
                </select>
                <select
                  value={bookFilterType}
                  onChange={(e) => setBookFilterType(e.target.value)}
                  className="px-3 py-2 border rounded-lg text-sm bg-white"
                  data-testid="booking-filter-transfer-type"
                >
                  <option value="all">Tutti i tipi</option>
                  <option value="instant">Istantanei</option>
                  <option value="ordinary">Ordinari</option>
                </select>
                <Button
                  size="sm"
                  variant={showBookFilters ? 'default' : 'outline'}
                  onClick={() => setShowBookFilters(!showBookFilters)}
                  data-testid="booking-toggle-advanced-filters"
                >
                  <Filter className="h-3.5 w-3.5 mr-1" /> Avanzati
                </Button>
                {(bookSearch || bookFilterStatus !== 'all' || bookFilterMonth !== 'all' || bookFilterType !== 'all' || bookAmountMin || bookAmountMax || bookDateFrom || bookDateTo) && (
                  <Button size="sm" variant="ghost" onClick={() => {
                    setBookSearch(''); setBookFilterStatus('all'); setBookFilterMonth('all'); setBookFilterType('all');
                    setBookAmountMin(''); setBookAmountMax(''); setBookDateFrom(''); setBookDateTo('');
                  }}>
                    <X className="h-3.5 w-3.5 mr-1" /> Reset
                  </Button>
                )}
              </div>
              {showBookFilters && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 pt-3 border-t">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Importo min (€)</label>
                    <input type="number" value={bookAmountMin} onChange={(e) => setBookAmountMin(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1" placeholder="0" data-testid="booking-amount-min" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Importo max (€)</label>
                    <input type="number" value={bookAmountMax} onChange={(e) => setBookAmountMax(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1" placeholder="∞" data-testid="booking-amount-max" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Data dal</label>
                    <input type="date" value={bookDateFrom} onChange={(e) => setBookDateFrom(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1" data-testid="booking-date-from" />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Data al</label>
                    <input type="date" value={bookDateTo} onChange={(e) => setBookDateTo(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm mt-1" data-testid="booking-date-to" />
                  </div>
                </div>
              )}
            </Card>

            {ownerLoading ? (
              <div className="text-center py-12 text-slate-500"><Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />Caricamento...</div>
            ) : (() => {
              // Apply filters
              const q = bookSearch.toLowerCase().trim();
              const minAmt = parseFloat(bookAmountMin) || 0;
              const maxAmt = parseFloat(bookAmountMax) || Infinity;
              const filtered = bookingBatches.filter(b => {
                // Status
                if (bookFilterStatus === 'pending' && (b.status || '').toLowerCase() === 'executed') return false;
                if (bookFilterStatus === 'executed' && (b.status || '').toLowerCase() !== 'executed') return false;
                // Tipo bonifico
                if (bookFilterType !== 'all' && (b.transfer_type || '') !== bookFilterType) return false;
                // Month (su created_at)
                if (bookFilterMonth !== 'all') {
                  const cd = b.created_at ? new Date(b.created_at) : null;
                  if (!cd || cd.getMonth() + 1 !== parseInt(bookFilterMonth, 10)) return false;
                }
                // Amount
                const amt = parseFloat(b.total_amount) || 0;
                if (amt < minAmt || amt > maxAmt) return false;
                // Date range (created_at)
                if (bookDateFrom && b.created_at) {
                  if (new Date(b.created_at) < new Date(bookDateFrom)) return false;
                }
                if (bookDateTo && b.created_at) {
                  if (new Date(b.created_at) > new Date(bookDateTo + 'T23:59:59')) return false;
                }
                // Free text: cerca su molti campi
                if (q) {
                  const hay = [
                    b.id, b.batch_type, b.status, b.invoice_reference,
                    String(b.total_amount), String(b.num_payments || b.num_transactions),
                    String(b.num_beneficiaries),
                    b.created_at, b.executed_at,
                    ...(b.transactions || []).flatMap(t => [t.beneficiary, t.iban, t.reference, t.causale]),
                  ].filter(Boolean).join(' ').toLowerCase();
                  if (!hay.includes(q)) return false;
                }
                return true;
              });
              if (filtered.length === 0) {
                return (
                  <Card className="p-8 text-center text-slate-400">
                    <Archive className="h-8 w-8 mx-auto mb-2" />
                    <p>{bookingBatches.length === 0 ? `Nessun pagamento Booking.com per ${year}` : 'Nessun risultato per i filtri impostati'}</p>
                  </Card>
                );
              }
              return (
                <>
                  <div className="text-xs text-slate-500 mb-2">{filtered.length} di {bookingBatches.length} batch · totale filtrato <strong>€ {filtered.reduce((s,b) => s + (parseFloat(b.total_amount) || 0), 0).toFixed(2)}</strong></div>
                  <div className="space-y-2">
                    {filtered.map(b => renderBatchRow(b, { onDelete: handleOwnerDelete, onMarkExecuted: handleOwnerMarkExecuted, onUploadResponse: handleOwnerUploadResponse, onDownload: handleOwnerDownload, onViewPdf: handleOwnerViewPdf, isOwner: true }))}
                  </div>
                </>
              );
            })()}
          </>
        )}

        {/* ═══ PAYPAL TAB ═══ */}
        {activeTab === 'paypal' && (
          <>
            {yearlyTotals && (
              <Card className="p-3 mb-3 bg-emerald-50 border-emerald-200">
                <div className="flex items-center gap-2 text-emerald-900">
                  <span className="text-sm">Totale PayPal versato {year}:</span>
                  <span className="text-xl font-bold">€ {Number(yearlyTotals.paypal_total || 0).toFixed(2).replace('.', ',')}</span>
                </div>
              </Card>
            )}
            <PayPalOwnerPaymentsTab readOnly={!canMutatePaypal} />
          </>
        )}

        {/* ═══ TASSA DI SOGGIORNO (IDS) TAB ═══ */}
        {activeTab === 'ids' && (
          <TouristTaxArchiveTab year={year} />
        )}

        {/* ═══ INTERCOMPANY TAB ═══ */}
        {activeTab === 'intercompany' && isAdmin && (
          <IntercompanyChargesTab year={year} />
        )}

        {/* ═══ RICOSTRUZIONE PROPRIETARIO TAB ═══ */}
        {activeTab === 'reconstruction' && (
          <OwnerReconstructionTab year={year} />
        )}

        {/* Edit Stay Dates Modal */}
        {editingDates && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setEditingDates(null)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="font-semibold text-slate-900">Date soggiorno</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-slate-600">Dal</label><input type="date" value={editFrom} onChange={(e) => setEditFrom(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" /></div>
                <div><label className="text-xs font-medium text-slate-600">Al</label><input type="date" value={editTo} onChange={(e) => setEditTo(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm mt-1" /></div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="outline" onClick={() => setEditingDates(null)}>Annulla</Button>
                <Button size="sm" onClick={() => handleSaveDates(editingDates)}>Salva</Button>
              </div>
            </div>
          </div>
        )}

        <PdfPreviewModal pdfPreview={pdfPreview} onClose={() => { if (pdfPreview) URL.revokeObjectURL(pdfPreview.url); setPdfPreview(null); }} />
        {showDupCheck && <DuplicatesCheckModal autoPrint={dupAutoPrint} onClose={() => setShowDupCheck(false)} />}
      </div>
    </Layout>
  );
}
